import React, { useState } from "react";
import { Button, Col, Form, Row, Stack } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Controller, UseFormReturn } from "react-hook-form";
import Select from "react-select";

import classNames from "classnames";

import useCreateGameHandlers from "../hooks/useCreateGameHandlers";
import { ICreateGameData } from "../types/creategame";

import Loading from "../../../components/Loading";
import { selectSelectedTeam } from "../../../features/clubhouse/clubhouseSlice";
import {
    selectFields,
    selectSetting,
} from "../../../features/global/globalSlice";
import { useAppSelector } from "../../../hooks/reduxHooks";
import parseDate from "../../../lib/parseDate";
import { displayFormAlert } from "../../../lib/utils";
import { IGame, ILeague, ISiteSetting, ITeam } from "../../../types/api";

const CreateGameForm = ({
    methods,
    schedule,
    setSchedule,
    league,
    isOnLeagueManagementPage,
}: {
    methods: UseFormReturn<ICreateGameData>;
    schedule: IGame[];
    setSchedule: React.Dispatch<React.SetStateAction<IGame[]>>;
    league: ILeague;
    isOnLeagueManagementPage: boolean;
}): React.JSX.Element => {
    const [sending, setSending] = useState<boolean>(false);

    const fields = useAppSelector(selectFields);
    const { setting: leagueStartSetting } = useAppSelector(state =>
        selectSetting(state, "majorybl_season_start_date"),
    ) as ISiteSetting;
    const { setting: leagueEndSetting } = useAppSelector(state =>
        selectSetting(state, "majorybl_season_end_date"),
    ) as ISiteSetting;
    const selectedTeam = useAppSelector(selectSelectedTeam);

    const leagueStartDate = parseDate(leagueStartSetting);
    const leagueEndDate = parseDate(leagueEndSetting);

    const { onSubmit } = useCreateGameHandlers(
        methods.reset,
        league.id,
        setSending,
        setSchedule,
        isOnLeagueManagementPage,
    );

    const watchField = methods.watch("field");
    const fieldDiamonds =
        watchField &&
        fields.find(f => parseInt(f.id.toString()) === watchField.value)
            ?.diamonds;

    const postponed: Date[] = [];
    const scheduled: Date[] = [];
    const rescheduled: Date[] = [];

    schedule.forEach(g => {
        switch (g.status) {
            case "Scheduled":
                scheduled.push(new Date(g.date));
                break;
            case "Postponed":
                postponed.push(new Date(g.date));
                break;
            case "Rescheduled":
                rescheduled.push(new Date(g.date));
                break;
        }
    });

    return (
        <Form
            onSubmit={methods.handleSubmit(onSubmit)}
            id="createGameForm"
            data-testid="createGameForm"
        >
            <Row>
                <Form.Group
                    controlId="datetime"
                    as={Col}
                    md={3}
                    data-testid="datetime"
                >
                    <Form.Label id="datetime">Date/Time *</Form.Label>
                    <Controller
                        control={methods.control}
                        name="datetime"
                        rules={{
                            required:
                                "Please select the date/time for the game",
                        }}
                        render={({ field: { onChange, onBlur, value } }) => (
                            <DatePicker
                                ariaLabelledBy="datetime"
                                selected={value}
                                showMonthDropdown
                                showYearDropdown
                                showTimeSelect
                                timeFormat="h:mm aa"
                                timeIntervals={15}
                                timeCaption="Time"
                                dateFormat="MMMM d, yyyy h:mm aa"
                                dropdownMode="select"
                                className="form-control"
                                minDate={leagueStartDate}
                                maxDate={leagueEndDate}
                                minTime={new Date(0, 0, 0, 6, 0)}
                                maxTime={new Date(0, 0, 0, 21, 0)}
                                onChange={onChange}
                                onBlur={onBlur}
                                highlightDates={[
                                    {
                                        "react-datepicker__day--highlighted-postponed":
                                            postponed,
                                    },
                                    {
                                        "react-datepicker__day--highlighted-scheduled":
                                            scheduled,
                                    },
                                    {
                                        "react-datepicker__day--highlighted-rescheduled":
                                            rescheduled,
                                    },
                                ]}
                            />
                        )}
                    />
                    {methods.formState.errors.datetime &&
                        displayFormAlert({
                            level: "danger",
                            text: methods.formState.errors.datetime
                                .message as string,
                            settings: "mb-0",
                        })}
                </Form.Group>
                <Col className="dateLabels">
                    <h5>Highlighted Dates:</h5>
                    <Stack direction="horizontal" gap={2}>
                        <span className="react-datepicker__day--highlighted-scheduled">
                            Scheduled Game
                        </span>
                        <span className="react-datepicker__day--highlighted-rescheduled">
                            Rescheduled Game
                        </span>
                        <span className="react-datepicker__day--highlighted-postponed">
                            Postponed Game
                        </span>
                        <span className="react-datepicker__day--selected">
                            Selected Date
                        </span>
                    </Stack>
                </Col>
            </Row>
            <Row>
                <Form.Group
                    controlId="field"
                    as={Col}
                    md={6}
                    data-testid="field"
                >
                    <Form.Label id="field">Field *</Form.Label>
                    <Controller
                        name="field"
                        control={methods.control}
                        rules={{
                            required: "Please select a field",
                        }}
                        render={({ field }) => (
                            <Select
                                {...field}
                                aria-labelledby="field"
                                isSearchable={true}
                                value={field.value || null}
                                onChange={selectedOption =>
                                    field.onChange(selectedOption)
                                }
                                options={fields
                                    .filter(f => f.active === 1)
                                    .filter(f => f.id !== 532)
                                    .sort((a, b) => {
                                        return a.name.localeCompare(b.name);
                                    })
                                    .map(f => {
                                        return {
                                            value: parseInt(f.id.toString()),
                                            label: `${f.name} (${f.city}, ${f.state})`,
                                        };
                                    })}
                            />
                        )}
                    />
                    {methods.formState.errors.field &&
                        displayFormAlert({
                            level: "danger",
                            text: methods.formState.errors.field.message,
                        })}
                </Form.Group>
                <Form.Group controlId="note" as={Col} xs={6} lg={4} xl={3}>
                    <Form.Label>Field Note</Form.Label>
                    <Form.Control
                        type="text"
                        {...methods.register("note", {
                            maxLength: {
                                value: 255,
                                message:
                                    "Please limit the field note to 255 characters or less",
                            },
                        })}
                        aria-invalid={
                            methods.formState.errors.note ? "true" : "false"
                        }
                    />
                    {methods.formState.errors.note &&
                        displayFormAlert({
                            level: "danger",
                            text: methods.formState.errors.note
                                .message as string,
                        })}
                </Form.Group>
                {watchField && fieldDiamonds && fieldDiamonds.length > 0 && (
                    <Form.Group
                        controlId="diamond"
                        as={Col}
                        md={3}
                        data-testid="diamond"
                    >
                        <Form.Label id="diamond">Diamond #</Form.Label>
                        <Controller
                            name="diamond"
                            control={methods.control}
                            rules={{
                                required:
                                    "A diamond is required for this field",
                            }}
                            render={({ field }) => (
                                <Select
                                    {...field}
                                    value={field.value || null}
                                    onChange={selectedOption =>
                                        field.onChange(selectedOption)
                                    }
                                    aria-labelledby="diamond"
                                    isSearchable={false}
                                    options={fieldDiamonds.map(d => {
                                        return {
                                            value: parseInt(d.id.toString()),
                                            label: d.name,
                                        };
                                    })}
                                />
                            )}
                        />
                        {methods.formState.errors.diamond &&
                            displayFormAlert({
                                level: "danger",
                                text: methods.formState.errors.diamond
                                    .message as string,
                            })}
                    </Form.Group>
                )}
                {isOnLeagueManagementPage ? (
                    <Form.Group
                        controlId="home"
                        as={Col}
                        md={{ span: 6, order: "last" }}
                        xs={{ order: "first" }}
                        data-testid="home"
                    >
                        <Form.Label id="home">Home Team *</Form.Label>
                        <Controller
                            name="home"
                            control={methods.control}
                            rules={{
                                required: "Please select the home team",
                            }}
                            render={({ field }) => (
                                <Select
                                    {...field}
                                    aria-labelledby="home"
                                    isSearchable={false}
                                    value={field.value || null}
                                    onChange={selectedOption =>
                                        field.onChange(selectedOption)
                                    }
                                    options={league?.teams?.map(t => {
                                        return {
                                            value: parseInt(
                                                t.id.toString(),
                                            ) as number,
                                            label: t.name as string,
                                        };
                                    })}
                                />
                            )}
                        />
                        {methods.formState.errors.home &&
                            displayFormAlert({
                                level: "danger",
                                text: methods.formState.errors.home.message,
                            })}
                    </Form.Group>
                ) : (
                    <Form.Control
                        hidden
                        {...methods.register("home")}
                        readOnly
                    />
                )}
                <Form.Group
                    controlId="visitor"
                    as={Col}
                    md={{ span: 6, order: "last" }}
                    xs={{ order: "first" }}
                    data-testid="visitor"
                >
                    <Form.Label id="visitor">Away Team *</Form.Label>
                    <Controller
                        name="visitor"
                        control={methods.control}
                        rules={{
                            required: "Please select the visiting team",
                        }}
                        render={({ field }) => (
                            <Select
                                {...field}
                                aria-labelledby="visitor"
                                isSearchable={false}
                                value={field.value || null}
                                onChange={selectedOption =>
                                    field.onChange(selectedOption)
                                }
                                options={league?.teams
                                    ?.filter(t => {
                                        return isOnLeagueManagementPage
                                            ? true
                                            : t.id !== selectedTeam?.id;
                                    })
                                    .map(t => {
                                        return {
                                            value: parseInt(
                                                t.id.toString(),
                                            ) as number,
                                            label: t.name as string,
                                        };
                                    })}
                            />
                        )}
                    />
                    {methods.formState.errors.visitor &&
                        displayFormAlert({
                            level: "danger",
                            text: methods.formState.errors.visitor.message,
                        })}
                </Form.Group>
            </Row>
            <Row>
                <Col className={classNames("d-flex", "align-items-baseline")}>
                    <Button variant="primary" type="submit" disabled={sending}>
                        Add Game
                    </Button>
                    {sending && <Loading className="mx-3 w-auto" />}
                </Col>
            </Row>
        </Form>
    );
};

export default CreateGameForm;
